import React, { useEffect, useState } from 'react';
import { View, TouchableOpacity, StyleSheet, Modal, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';

export interface LedgerFilterOptions {
  timeframe: 'all' | 'today' | 'week' | 'month' | 'year';
  amountRange: 'all' | 'under-1000' | '1000-10000' | 'over-10000';
  transactionType: 'all' | 'received' | 'paid';
}

interface LedgerFiltersProps {
  filters: LedgerFilterOptions;
  onFiltersChange: (filters: LedgerFilterOptions) => void;
  visible: boolean;
  onClose: () => void;
}

const timeframes: { label: string; value: LedgerFilterOptions['timeframe'] }[] = [
  { label: 'All Time', value: 'all' },
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'This Year', value: 'year' },
];

const amountRanges: { label: string; value: LedgerFilterOptions['amountRange'] }[] = [
  { label: 'All Amounts', value: 'all' },
  { label: 'Under 1,000', value: 'under-1000' },
  { label: '1,000 - 10,000', value: '1000-10000' },
  { label: 'Over 10,000', value: 'over-10000' },
];

const transactionTypes: { label: string; value: LedgerFilterOptions['transactionType'] }[] = [
  { label: 'All Transactions', value: 'all' },
  { label: 'Received (Credit)', value: 'received' },
  { label: 'Paid (Debit)', value: 'paid' },
];

const LedgerFilters: React.FC<LedgerFiltersProps> = ({ filters, onFiltersChange, visible, onClose }) => {
  const { theme } = useTheme();
  const [tempFilters, setTempFilters] = useState<LedgerFilterOptions>(filters);

  useEffect(() => {
    if (visible) {
      setTempFilters(filters);
    }
  }, [filters, visible]);

  const handleApply = () => {
    onFiltersChange(tempFilters);
    onClose();
  };

  const handleReset = () => {
    const resetFilters: LedgerFilterOptions = {
      timeframe: 'all',
      amountRange: 'all',
      transactionType: 'all',
    };
    setTempFilters(resetFilters);
    onFiltersChange(resetFilters);
    onClose();
  };

  const renderChipGroup = (
    title: string,
    options: { label: string; value: LedgerFilterOptions[keyof LedgerFilterOptions] }[],
    selectedValue: string,
    onSelect: (value: string) => void,
  ) => (
    <View style={styles.filterSection}>
      <ThemedText style={[styles.filterLabel, { color: theme.text }]}>{title}</ThemedText>
      <View style={styles.filterOptionsRow}>
        {options.map((option) => {
          const isSelected = selectedValue === option.value;
          return (
            <TouchableOpacity
              key={option.value}
              style={[
                styles.filterChip,
                {
                  backgroundColor: isSelected ? theme.accent : theme.backgroundTertiary,
                  borderColor: isSelected ? theme.accent : theme.border,
                },
              ]}
              onPress={() => onSelect(option.value)}
              activeOpacity={0.7}
            >
              <ThemedText
                style={[
                  styles.filterChipText,
                  { color: isSelected ? theme.textInverse : theme.text },
                ]}
              >
                {option.label}
              </ThemedText>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );

  return (
    <Modal
      animationType="slide"
      transparent
      visible={visible}
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.modalContent, { backgroundColor: theme.backgroundSecondary }]}>
          <View style={[styles.modalHeader, { borderBottomColor: theme.border }]}> 
            <ThemedText style={[styles.modalTitle, { color: theme.text }]}>Filters</ThemedText>
            <TouchableOpacity onPress={onClose} activeOpacity={0.7}>
              <Ionicons name="close" size={24} color={theme.text} />
            </TouchableOpacity>
          </View>

          <ScrollView
            style={styles.modalBody}
            contentContainerStyle={{ paddingBottom: 8 }}
            showsVerticalScrollIndicator={false}
          >
            {renderChipGroup('Time Period', timeframes, tempFilters.timeframe, (value) =>
              setTempFilters({ ...tempFilters, timeframe: value as LedgerFilterOptions['timeframe'] }),
            )}

            {renderChipGroup('Amount Range', amountRanges, tempFilters.amountRange, (value) =>
              setTempFilters({ ...tempFilters, amountRange: value as LedgerFilterOptions['amountRange'] }),
            )}

            {renderChipGroup('Transaction Type', transactionTypes, tempFilters.transactionType, (value) =>
              setTempFilters({ ...tempFilters, transactionType: value as LedgerFilterOptions['transactionType'] }),
            )}
          </ScrollView>

          <SafeAreaView edges={['bottom']} style={{ backgroundColor: theme.backgroundSecondary }}>
            <View style={[styles.modalFooter, { borderTopColor: theme.border }]}> 
              <TouchableOpacity
                style={[styles.resetButton, { borderColor: theme.border }]}
                onPress={handleReset}
                activeOpacity={0.7}
              >
                <ThemedText style={[styles.resetButtonText, { color: theme.textSecondary }]}>Reset</ThemedText>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.applyButton, { backgroundColor: theme.accent }]}
                onPress={handleApply}
                activeOpacity={0.7}
              >
                <ThemedText style={[styles.applyButtonText, { color: theme.textInverse }]}>Apply Filters</ThemedText>
              </TouchableOpacity>
            </View>
          </SafeAreaView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  modalBody: {
    paddingVertical: 20,
  },
  filterSection: {
    marginBottom: 24,
    paddingHorizontal: 20,
  },
  filterLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  filterOptionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterChip: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    borderWidth: 1,
  },
  filterChipText: {
    fontSize: 14,
    fontWeight: '500',
  },
  modalFooter: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    gap: 12,
  },
  resetButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
  },
  resetButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  applyButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  applyButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default LedgerFilters;
